import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { conditionalHooksFactory } from '@atlaskit/platform-feature-flags-react/conditional-hooks-factory';

import {
	CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY,
	CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
	isRemixGeneratedFlow,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import type { StoreChatItem } from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { findLastAssistantAction } from '@atlassian/conversation-assistant/controllers/chat-store/utils';
import { RemixContentComponent } from '@atlassian/malleable-ui-components/editor-remix-creator';
import type {
	RemixContentComponentProps,
	RemixInsertSuccessPayload,
	RemixProductConfig,
	RemixProductIntegration,
} from '@atlassian/malleable-ui-components/types';
import type { SmartCreationContext } from '@atlassian/conversation-assistant/ui/staging-area-modal';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { type WithFlagsProps, withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import {
	useSmartCreationModalActions,
	useSmartCreationModalState,
} from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import { useToggleConversationAssistant } from '@confluence/conversation-assistant/entry-points/useToggleConversationAssistant.ts';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useConversationAssistantConfig } from '@confluence/smart-create/entry-points/useConfig.ts';
import { FeedbackVariant } from '@confluence/smart-create/entry-points/FeedbackVariant.ts';
import { useLazyShowFeedbackFlag } from '@confluence/smart-create/entry-points/useLazyShowFeedbackFlag.ts';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

const confluenceProductConfig: RemixProductConfig = {
	productKey: 'confluence',
	analyticsProductName: 'confluence',
	shouldUseEphemeralPreview: (chatMessages: StoreChatItem[]): boolean => {
		for (let i = chatMessages.length - 1; i >= 0; i--) {
			const message = chatMessages[i];
			if (message.type === 'ACTION_PREPARE') {
				return message.action ? isRemixGeneratedFlow(message.action) : false;
			}
		}
		return false;
	},
	isActionReady: (chatMessages: StoreChatItem[]): boolean => {
		const hasWhiteboard =
			findLastAssistantAction(
				chatMessages,
				(action) =>
					action.key === CONFLUENCE_AIFC_WHITEBOARD_CREATE_KEY ||
					action.key === CONFLUENCE_AIFC_WHITEBOARD_UPDATE_KEY,
			) !== undefined;
		if (hasWhiteboard) {
			return true;
		}
		for (let i = chatMessages.length - 1; i >= 0; i--) {
			const message = chatMessages[i];
			if (message.type === 'ACTION_COMPLETE') {
				return true;
			}
			if (message.type === 'HUMAN') {
				return false;
			}
		}
		return false;
	},
};

const useConfluenceProductIntegration = ({
	flags,
}: {
	flags: FlagsStateContainer;
}): RemixProductIntegration => {
	const { showSmartCreationModal } = useSmartCreationModalState();
	const { openModal, closeModal } = useSmartCreationModalActions();
	const { toggleConversationAssistant: toggleAssistant } = useToggleConversationAssistant();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { showModal } = useDialogs();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const pageMode = useGetPageMode();
	const { cloudId } = useSessionData();
	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	const onRemixInsertSuccess = useCallback(
		({ currentConversationId, remixMetadata }: RemixInsertSuccessPayload) => {
			void showCreateWithRovoModalCloseFeedbackFlag({
				feedbackVariant: FeedbackVariant.AFTER_REMIX_INSERT,
				contentType,
				contentMode: contentType === 'page' ? pageMode : undefined,
				cloudId,
				currentConversationId,
				remixMetadata,
			});
		},
		[showCreateWithRovoModalCloseFeedbackFlag, contentType, pageMode, cloudId],
	);

	return useMemo<RemixProductIntegration>(
		() => ({
			contentType: contentType ?? 'page',
			isRemixPreviewVisible: showSmartCreationModal,
			onRemixInsertSuccess,
			openRemixPreview: ({ isFloating, remixConversationId }) => {
				openModal({
					entryPoint: 'dynamicUi',
					spaceKey: spaceKey ?? undefined,
					stagingAreaActive: true,
					isFloating,
					shouldShowBackButton: false,
					...(remixConversationId && expValEquals('cc-maui-experiment', 'isEnabled', true)
						? { remixConversationId }
						: {}),
					content: contentId
						? {
								id: contentId,
								type: contentType ?? 'page',
								subType: isLivePage ? 'live' : null,
							}
						: undefined,
					...(isFloating ? { submitCallback: async () => {} } : {}),
				});
			},
			closeRemixPreview: closeModal,
			toggleConversationAssistant: (location: string) => {
				toggleAssistant(location as 'sidebar' | 'mini-modal');
			},
		}),
		[
			showSmartCreationModal,
			openModal,
			closeModal,
			toggleAssistant,
			spaceKey,
			contentId,
			contentType,
			isLivePage,
			onRemixInsertSuccess,
		],
	);
};

const useConfig = () => {
	const { cloudId } = useSessionData();

	return useConversationAssistantConfig({
		assistanceServiceConfigHeaders: {
			'X-Cloudid': cloudId,
		},
		preventAbortRequestsOnStagingModalClose: true,
	});
};

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useConfigOld() {
	return undefined;
}

const useConfigSafe = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useConfig,
	useConfigOld,
);

const useSmartCreationContext = (): SmartCreationContext => {
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const isLivePage = useIsCurrentPageLive();
	const { data: spaceDetail } = useSpaceDetail(spaceKey, {
		includeSpaceTypeSettings: expVal('aifc_space_typesettings', 'isEnabled', false),
	});

	const context: SmartCreationContext = {
		confluence: {
			space: spaceDetail
				? {
						id: spaceDetail.spaceId,
						name: spaceDetail.name,
						// `key` is needed to construct correct whiteboard URLs
						// (`/wiki/spaces/{spaceKey}/whiteboard/...`). Without it,
						// `useOnComplete` falls back to `~{numericSpaceId}`, which
						// the page-level URL routing treats as a personal-space key
						// and resolves to the wrong space.
						key: spaceKey ?? undefined,
						iconPath: spaceDetail.iconPath,
						typeSettings: spaceDetail.typeSettings,
					}
				: undefined,
			parentContent: spaceDetail
				? {
						id: spaceDetail.homepage?.id ?? '',
						title: spaceDetail.homepage?.title ?? '',
						contentType: spaceDetail.homepage?.type ?? '',
					}
				: undefined,
			content: contentId
				? {
						id: contentId,
						type: contentType ?? 'page',
						subType: isLivePage ? 'live' : null,
					}
				: undefined,
		},
	};

	return context;
};

// remove this when cleanup platform_editor_ai_remix_in_editor_loading
function useSmartCreationContextOld() {
	return undefined;
}

const useSmartCreationContextSafe = conditionalHooksFactory(
	() => fg('platform_editor_ai_remix_in_editor_loading'),
	useSmartCreationContext,
	useSmartCreationContextOld,
);

const PlatformComponentInner = ({
	flags,
	...props
}: RemixContentComponentProps & WithFlagsProps) => {
	const productIntegration = useConfluenceProductIntegration({ flags });
	const smartCreationConfig = useConfigSafe();
	const smartCreationContext = useSmartCreationContextSafe();

	return (
		<RemixContentComponent
			{...props}
			entryPoint={fg('platform_editor_ai_remix_in_editor_loading') ? 'dynamicUi' : undefined}
			smartCreationConfig={
				fg('platform_editor_ai_remix_in_editor_loading') ? smartCreationConfig : undefined
			}
			smartCreationContext={
				fg('platform_editor_ai_remix_in_editor_loading') ? smartCreationContext : undefined
			}
			productConfig={confluenceProductConfig}
			productIntegration={productIntegration}
		/>
	);
};

const PlatformComponent = withFlags(PlatformComponentInner);

export const createRemixContentComponentPlatform = (): ((
	props: RemixContentComponentProps,
) => React.JSX.Element | null) => {
	const Wrapper = (props: RemixContentComponentProps) => {
		const { onStopThinking, isThinking, isModalOpen } = props;
		const { showSmartCreationModal } = useSmartCreationModalState();
		const isSmartCreationModalOpen = useRef(false);

		useLayoutEffect(() => {
			if (fg('platform_editor_ai_remix_in_editor_loading')) {
				return;
			}
			if (isSmartCreationModalOpen.current && !showSmartCreationModal && isThinking) {
				onStopThinking();
			}
			isSmartCreationModalOpen.current = showSmartCreationModal;
		}, [showSmartCreationModal, isThinking, onStopThinking]);

		if (fg('platform_editor_ai_remix_in_editor_loading')) {
			return (
				<ConfluenceConversationAssistant>
					<PlatformComponent {...props} />
				</ConfluenceConversationAssistant>
			);
		}

		return isModalOpen || isThinking ? (
			<ConfluenceConversationAssistant>
				<PlatformComponent {...props} />
			</ConfluenceConversationAssistant>
		) : null;
	};

	return Wrapper;
};
